import keycloak from '../config/keycloak';
import type { User } from '../types';

export class AuthService {
  /**
   * Get the current authenticated user from Keycloak token
   */
  getCurrentUser(): User | null {
    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      return null;
    }

    const token = keycloak.tokenParsed as any;

    return {
      id: token.sub,
      externalUserId: token.sub,
      email: token.email || '',
      displayName: token.name || token.preferred_username || token.email || '',
    };
  }

  /**
   * Get the current JWT access token
   */
  getAccessToken(): string | undefined {
    return keycloak.token;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return keycloak.authenticated || false;
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    try {
      await keycloak.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  /**
   * Refresh the token if needed
   */
  async updateToken(minValidity: number = 30): Promise<boolean> {
    try {
      const refreshed = await keycloak.updateToken(minValidity);
      return refreshed;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Get user roles from token
   */
  getUserRoles(): string[] {
    if (!keycloak.tokenParsed) {
      return [];
    }

    const token = keycloak.tokenParsed as any;
    return token.realm_access?.roles || [];
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }
}

// Export singleton instance
export const authService = new AuthService();
